import { set } from '@vueuse/core';
import { type } from 'arktype';
import { ref, shallowRef, toValue, watch } from 'vue';
import {
  type AddressDetails,
  AddressDetailsResource,
  AddressDetailsResourceSchema,
  type AddressSearchResource,
  AddressSearchResourceListSchema,
} from '../address-auto-complete.types.js';

const baseUrl = 'https://florists-new.hijlkemacodes.app/api/florists/v1/address-autocomplete';
const session = ref<string | undefined>(undefined);
let abortController = new AbortController();
const headers = new Headers();
headers.set('Accept', 'application/json');

type CallState = 'loading' | 'error' | 'done' | 'default';

export function useAutocompleteSearch() {
  const status = ref<CallState>('default');
  const formattedMailLines = ref<string[]>([]);
  const mailLines = ref<AddressDetails | undefined>(undefined);
  const suggestions = shallowRef<AddressSearchResource[]>([]);
  const details = shallowRef<AddressDetailsResource | undefined>(undefined);

  watch(session, (sessionToken): void => {
    if (sessionToken) {
      headers.set('X-Autocomplete-Session', sessionToken);
    }
    // todo: Add refresh here
  });

  function setFailed(...data: unknown[]) {
    console.error('Failed to data', data);
    set(status, 'error');
  }

  /**
   * @todo doc
   */
  async function startSearching(url: URL) {
    if (toValue(status) === 'loading') {
      abortController.abort();
    }

    set(status, 'loading');
    set(formattedMailLines, []);
    set(mailLines, undefined);
    set(suggestions, []);
    set(details, undefined);

    let res: Response;
    try {
      abortController = new AbortController();
      res = await fetch(url, { headers, signal: abortController.signal });
    } catch {
      clear();
      return;
    }

    if (!res.ok) {
      return setFailed();
    }

    const arkBody = AddressSearchResourceListSchema(await res.json());
    if (arkBody instanceof type.errors) {
      return setFailed(arkBody.flatProblemsByPath);
    }

    if (arkBody.session) {
      set(session, arkBody.session);
    }

    set(suggestions, arkBody.data);
    set(status, 'done');
  }

  /**
   * Load specific details for the user.
   */
  async function loadDetails(url: URL) {
    if (toValue(status) === 'loading') {
      abortController.abort();
    }

    set(status, 'loading');
    let res: Response;
    try {
      abortController = new AbortController();
      res = await fetch(url, { headers, signal: abortController.signal });
    } catch {
      return;
    }

    if (!res.ok) {
      return setFailed();
    }

    const arkBody = AddressDetailsResourceSchema(await res.json());
    if (arkBody instanceof type.errors) {
      return setFailed(arkBody.flatProblemsByPath);
    }

    set(mailLines, arkBody.address);
    set(formattedMailLines, arkBody.mail_lines);
    set(details, arkBody);
    set(status, 'done');
  }

  /**
   * Search for an address using a search query. This should be triggered on entry by the user.
   */
  async function searchAddress(query: string) {
    const url = new URL(`${baseUrl}/start`);
    url.searchParams.set('term', query);

    await startSearching(url);
  }

  /**
   * Get the details for a specific address search resource.
   *
   * If the supplied resource is not an end resource it will continue the search using the
   * continue-search link provided by the server.
   */
  async function getDetails(resource: AddressSearchResource) {
    if (resource.links['continue-search']) {
      const url = new URL(ensureUrl(resource.links['continue-search']));
      return startSearching(url);
    } else if (resource.links.details) {
      const url = new URL(ensureUrl(resource.links.details));

      return loadDetails(url);
    } else {
      // todo: another error here...
    }
  }

  function clear(): void {
    abortController.abort();
    set(status, 'default');
    set(mailLines, undefined);
    set(formattedMailLines, []);
    set(suggestions, []);
    set(details, undefined);
  }

  return { status, details, formattedMailLines, mailLines, suggestions, searchAddress, getDetails, clear };
}

function ensureUrl(maybeFullUrl: string): URL {
  if (maybeFullUrl.startsWith('https://')) {
    return new URL(maybeFullUrl);
  }

  return new URL(`${baseUrl.replace('/api/florists/v1/address-autocomplete', '')}${maybeFullUrl}`);
}
